'use client';

import React, { useRef, useEffect, useMemo } from 'react';
import { PixelStreamingWrapper } from '@/components/pixel-streaming/pixel-streaming-wrapper';
import { MicButton } from '@/components/MicButton';
import { QRCodeSVG } from 'qrcode.react';
import { CourseCardsOverlay } from '@/components/overlay/CourseCardsOverlay';
import { GoalCardsOverlay } from '@/components/overlay/GoalCardsOverlay';
import { KeywordOverlay } from '@/components/overlay/KeywordOverlay';
import { ProgressTracker } from '@/components/overlay/ProgressTracker';
import { PhaseBanner } from '@/components/overlay/PhaseBanner';
import { SessionTimer } from '@/components/overlay/SessionTimer';
import { SubtitleOverlay } from '@/components/overlay/SubtitleOverlay';


import '@/styles/app/home.css';
import { useStore } from '@/stores/store-provider';
import type { CourseCardOverlayItem } from '@/stores/digital-human-store';
import { ConnectScreen } from '@/components/screen/connect-screen';
import { observer } from 'mobx-react-lite';
import { StreamingStateEnum } from '@/data/webrtc-enums';
import useRealtime, { SessionConfig } from '@/hooks/useRealtime';
import { a2fStreamer } from '@/services/a2f-streamer';
import {
  initializeActiveUseCaseTools,
  getActiveInstructions,
  getActiveWelcomeMessage
} from '@/config/active-usecase';
import { getToolSchemas } from '@/tools';

const DEFAULT_REALTIME_SAMPLE_RATE = 24000;

export const Home = observer(() => {
  const store = useStore();
  const digitalHumanStore = store.digitalHumanStore;
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [audioError, setAudioError] = React.useState<string | null>(null);
  const [isStarting, setIsStarting] = React.useState(false);
  const responseSampleRate = useMemo(() => {
    const envRate = Number(process.env.NEXT_PUBLIC_REALTIME_SAMPLE_RATE);
    if (!Number.isFinite(envRate) || envRate <= 0) {
      return DEFAULT_REALTIME_SAMPLE_RATE;
    }
    return envRate;
  }, []);

  const [welcomeMessage, setWelcomeMessage] = React.useState(() =>
    getActiveWelcomeMessage((process.env.NEXT_PUBLIC_USE_CASE || 'provinzial').toLowerCase())
  );
  const [subtitleMode, setSubtitleMode] = React.useState<'off' | 'on' | 'keywords'>('off');
  const subtitleModeRef = useRef(subtitleMode);
  subtitleModeRef.current = subtitleMode;

  const [pendingToolHint, setPendingToolHint] = React.useState<string | null>(null);
  const pendingToolHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startSessionRef = useRef<((overrides?: { instructions?: string; tools?: any[] }) => void) | null>(null);

  const realtimeOptions = useMemo(
    () => ({
      onConnected: (session: SessionConfig) => {
        const useCase = session.useCase || 'provinzial';
        console.log(`[Home] Realtime connected — useCase: ${useCase}`);

        if (useCase === 'metro') {
          digitalHumanStore.startSessionTimer();
        }

        initializeActiveUseCaseTools(useCase);
        const instructions = getActiveInstructions(useCase);
        const tools = getToolSchemas();
        setWelcomeMessage(getActiveWelcomeMessage(useCase));

        console.log('[Home] Sending session.update');
        startSessionRef.current?.({ instructions, tools });
      },
      onResponseTranscriptDelta: (delta: string) => {
        if (subtitleModeRef.current === 'on') {
          digitalHumanStore.appendSubtitle(delta);
        }
      },
      onResponseAudioDelta: async (delta: string) => {
        digitalHumanStore.suppressMicDuringAssistantSpeech();
        await a2fStreamer.sendRealtimeAudioChunk(delta, responseSampleRate);
      },
      onResponseAudioDone: async () => {
        await a2fStreamer.finalizeStream();
        digitalHumanStore.releaseMicAfterAssistantSpeech();
      },
      onResponseDone: async () => {
        await a2fStreamer.finalizeStream();
        digitalHumanStore.releaseMicAfterAssistantSpeech();
      },
      onErrorMessage: (message: any) => {
        console.error('Realtime API error:', message);
        digitalHumanStore.releaseMicAfterAssistantSpeech();
      },
      onFunctionCallStarted: (name: string, callId: string) => {
        console.log(`🔧 Tool execution started: ${name} (${callId})`);
        if (name === 'get_cupra_recommendation') {
          if (pendingToolHintTimeoutRef.current) {
            clearTimeout(pendingToolHintTimeoutRef.current);
          }
          // Avoid flicker for fast calls; show hint only when call takes noticeable time.
          pendingToolHintTimeoutRef.current = setTimeout(() => {
            setPendingToolHint('Ich vergleiche kurz die besten Optionen fuer dich...');
          }, 600);
        }
      },
      onFunctionCallCompleted: (name: string, callId: string, result: any) => {
        console.log(`✅ Tool execution completed: ${name} (${callId})`);
        console.log('📊 Result:', result);
        if (name === 'get_cupra_recommendation') {
          if (pendingToolHintTimeoutRef.current) {
            clearTimeout(pendingToolHintTimeoutRef.current);
            pendingToolHintTimeoutRef.current = null;
          }
          setPendingToolHint(null);
        }
      },
      onFunctionCallError: (name: string, callId: string, error: string) => {
        console.error(`❌ Tool execution failed: ${name} (${callId})`, error);
        if (name === 'get_cupra_recommendation') {
          if (pendingToolHintTimeoutRef.current) {
            clearTimeout(pendingToolHintTimeoutRef.current);
            pendingToolHintTimeoutRef.current = null;
          }
          setPendingToolHint(null);
        }
      }
    }),
    [digitalHumanStore, responseSampleRate]
  );

  const { connectionStatus, startSession, addUserAudio, cancelAssistantResponse, triggerSpokenMessage, sendUserText } =
    useRealtime(realtimeOptions);

  startSessionRef.current = startSession;
  const triggerSpokenMessageRef = useRef(triggerSpokenMessage);
  triggerSpokenMessageRef.current = triggerSpokenMessage;
  const sendUserTextRef = useRef(sendUserText);
  sendUserTextRef.current = sendUserText;

  const handleCourseConfirmed = React.useCallback(
    (course: { courseId: string; title: string; summary: string; score?: number; goalIndex?: number }, goalIndex: number) => {
      digitalHumanStore.attachCourseToGoal(goalIndex, course.courseId, course.title);
      sendUserTextRef.current(`Ich wähle diesen Kurs: ${course.title}.`);
    },
    [digitalHumanStore]
  );

  // Enable presentation mode from URL so we don't claim the mic (e.g. when presenting in Teams)
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const presentation = params.get('presentation') === '1' || params.get('teams') === '1';
    if (presentation && typeof digitalHumanStore.setPresentationMode === 'function') {
      digitalHumanStore.setPresentationMode(true);
    }
  }, [digitalHumanStore]);

  // connect() is called internally by useRealtime's own useEffect.
  // startSession() is called from the onConnected callback above.

  const initializeMicAndConnect = React.useCallback(async (skipConnect = false) => {
    try {
      console.log('Starting audio initialization.');
      setAudioError(null);

      const isPresentationMode = digitalHumanStore.presentationMode;

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      if (isPresentationMode) {
        console.log('Presentation mode: skipping microphone access.');
        if (!skipConnect) digitalHumanStore.connect();
        return;
      }

      console.log('Requesting microphone via gated stream...');
      await digitalHumanStore.createGatedMediaStream();

      if (!skipConnect) {
        await new Promise(resolve => setTimeout(resolve, 500));
        digitalHumanStore.connect();
      }

      console.log('Audio initialization completed');
    } catch (error) {
      const err = error as { name: string; message: string; stack?: string };
      console.error('Audio initialization error:', err.name, err.message);

      if (err.name === 'NotAllowedError') {
        setAudioError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (err.name === 'NotFoundError') {
        setAudioError('No microphone found. Please connect a microphone and try again.');
      } else if (err.name === 'NotReadableError') {
        setAudioError('Microphone is busy or not readable.');
      } else {
        setAudioError(`Audio initialization failed: ${err.message}`);
      }

      if (!skipConnect) {
        digitalHumanStore.connect();
      }
    }
  }, [digitalHumanStore, setAudioError]);

  // Add this function to your Home component
  const testWebSocketConnection = React.useCallback(async () => {
    console.log('Testing WebSocket connection...');
    setAudioError('Testing WebSocket connection...');
    
    try {
      // Extract the WebSocket endpoint from environment variable
      const wsEndpoint = process.env.NEXT_PUBLIC_WS_ENDPOINT;
      console.log('WebSocket endpoint:', wsEndpoint);
      
      if (!wsEndpoint || wsEndpoint.trim() === '') {
        setAudioError('WebSocket endpoint not configured. Please check your environment variables.');
        return;
      }
      
      // Create a simple WebSocket connection
      const socket = new WebSocket(wsEndpoint);
      
      // Set a connection timeout
      const timeoutId = setTimeout(() => {
        console.error('WebSocket connection timed out');
        setAudioError('WebSocket connection timed out. The server might be unreachable.');
        socket.close();
      }, 10000); // 10 seconds timeout
      
      // Handle WebSocket events
      socket.onopen = () => {
        console.log('WebSocket connection established successfully');
        setAudioError('WebSocket connection successful! Proceeding with initialization...');
        clearTimeout(timeoutId);
        
        // Close the test socket
        socket.close();
        
        // Proceed with normal initialization after a short delay
        setTimeout(() => {
          initializeMicAndConnect();
        }, 1000);
      };
      
      socket.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        setAudioError('WebSocket connection error. Please check network settings.');
        clearTimeout(timeoutId);
      };
      
      socket.onclose = (event) => {
        console.log('WebSocket connection closed:', event.code, event.reason);
        if (event.code !== 1000) { // 1000 is normal closure
          setAudioError(`WebSocket closed abnormally. Code: ${event.code}, Reason: ${event.reason || 'No reason provided'}`);
        }
        clearTimeout(timeoutId);
      };
      
    } catch (error) {
      console.error('WebSocket test error:', error);
      const err = error as Error;
      setAudioError(`WebSocket test error: ${err.message}`);
    }
  }, [setAudioError, initializeMicAndConnect]);

  const onConnectClick = React.useCallback(() => {
    if (isStarting) return;
    setIsStarting(true);

    if (process.env.NEXT_PUBLIC_ARCWARE_SHARE_ID) {
      console.log('Connect button clicked (Arcware mode)');
      digitalHumanStore.setConnectionStatusMessage('Preparing your session…');
      initializeMicAndConnect();
    } else {
      console.log('Connect button clicked, testing WebSocket connection first...');
      testWebSocketConnection();
    }
  }, [testWebSocketConnection, initializeMicAndConnect, isStarting]);

  useEffect(() => {
    const streamer = digitalHumanStore.getStreamer?.();
    if (!streamer) {
      return;
    }

    const handleWebRtcConnected = () => {
      console.log('WebRTC connected event received');
      digitalHumanStore.setStreamingState(StreamingStateEnum.WebRtcConnected);
    };

    const handleVideoInitialized = () => {
      console.log('Video initialized event received at:', new Date().toISOString());
      console.log('Current streaming state:', digitalHumanStore.getStreamingState());
      console.log('Is already initialized:', isInitialized);

      digitalHumanStore.setStreamingState(StreamingStateEnum.StreamConnected);
      setIsInitialized(true);
    };

    streamer.addEventListener('webRtcConnected', handleWebRtcConnected);
    streamer.addEventListener('videoInitialized', handleVideoInitialized);

    return () => {
      streamer.removeEventListener('webRtcConnected', handleWebRtcConnected);
      streamer.removeEventListener('videoInitialized', handleVideoInitialized);
    };
  }, [digitalHumanStore, isInitialized]);

  useEffect(() => {
    const onShowCourseCards = (event: Event) => {
      const customEvent = event as CustomEvent<{
        cards?: Array<{ courseId: string; title: string; summary: string; score?: number }>;
        durationMs?: number;
        goalIndex?: number | null;
        goalTitle?: string | null;
      }>;
      const cards = Array.isArray(customEvent.detail?.cards) ? customEvent.detail.cards : [];
      const goalIndex = customEvent.detail?.goalIndex;
      const goalTitle = customEvent.detail?.goalTitle;
      if (typeof goalIndex === 'number') {
        // Auto-create goal card if it doesn't exist yet at this index
        if (goalTitle && !digitalHumanStore.goalCards[goalIndex]) {
          digitalHumanStore.addGoalCard(`goal_${goalIndex + 1}`, goalTitle);
        }
        digitalHumanStore.displayCourseCardsForGoal(cards, goalIndex);
      } else {
        void digitalHumanStore.displayCourseCards(cards);
      }
    };

    const onAddGoalCard = (event: Event) => {
      const customEvent = event as CustomEvent<{ goalId?: string; title?: string }>;
      const goalId = customEvent.detail?.goalId ?? '';
      const title = customEvent.detail?.title ?? '';
      if (goalId && title) {
        digitalHumanStore.addGoalCard(goalId, title);
      }
    };

    const onShowKeywords = (event: Event) => {
      const customEvent = event as CustomEvent<{
        keywords?: string[];
        durationMs?: number;
      }>;
      const keywords = Array.isArray(customEvent.detail?.keywords) ? customEvent.detail.keywords : [];
      const durationMs = typeof customEvent.detail?.durationMs === 'number' ? customEvent.detail.durationMs : 4000;
      void digitalHumanStore.showKeywords(keywords, durationMs);
    };

    const onSelectCourse = (event: Event) => {
      const customEvent = event as CustomEvent<{ courseId?: string }>;
      const courseId = typeof customEvent.detail?.courseId === 'string' ? customEvent.detail.courseId : '';
      if (courseId) {
        const course = digitalHumanStore.courseCards.find((c: CourseCardOverlayItem) => c.courseId === courseId);
        if (course && course.goalIndex !== undefined) {
          digitalHumanStore.attachCourseToGoal(course.goalIndex, course.courseId, course.title);
          sendUserTextRef.current(`Ich wähle diesen Kurs: ${course.title}.`);
        } else {
          digitalHumanStore.selectCourseCard(courseId);
        }
      }
    };

    const onPhaseChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{
        phaseIndex?: number;
        label?: string;
      }>;
      const phaseIndex = typeof customEvent.detail?.phaseIndex === 'number' ? customEvent.detail.phaseIndex : 0;
      const label = typeof customEvent.detail?.label === 'string' ? customEvent.detail.label : '';
      digitalHumanStore.startSessionTimer();
      digitalHumanStore.advanceCoachingPhase(phaseIndex, label);
    };

    const onRevealProgress = () => {
      digitalHumanStore.startSessionTimer();
      digitalHumanStore.revealProgressBar();
    };

    const onSpeechStarted = () => {
      // Keep goal cards — they are persistent anchors for the session
      if (!digitalHumanStore.goalCardsVisible) {
        digitalHumanStore.hideCourseCards();
      }
      digitalHumanStore.clearSubtitle();
      digitalHumanStore.hideKeywords();
    };

    window.addEventListener('metro-reveal-progress', onRevealProgress);
    window.addEventListener('metro-show-course-cards', onShowCourseCards as EventListener);
    window.addEventListener('metro-show-keywords', onShowKeywords as EventListener);
    window.addEventListener('metro-select-course', onSelectCourse as EventListener);
    window.addEventListener('metro-phase-changed', onPhaseChanged as EventListener);
    window.addEventListener('metro-speech-started', onSpeechStarted);
    window.addEventListener('metro-add-goal-card', onAddGoalCard as EventListener);

    return () => {
      window.removeEventListener('metro-reveal-progress', onRevealProgress);
      window.removeEventListener('metro-show-course-cards', onShowCourseCards as EventListener);
      window.removeEventListener('metro-show-keywords', onShowKeywords as EventListener);
      window.removeEventListener('metro-select-course', onSelectCourse as EventListener);
      window.removeEventListener('metro-phase-changed', onPhaseChanged as EventListener);
      window.removeEventListener('metro-speech-started', onSpeechStarted);
      window.removeEventListener('metro-add-goal-card', onAddGoalCard as EventListener);
    };
  }, [digitalHumanStore]);

  useEffect(() => {
    const onShowQr = (event: Event) => {
      const customEvent = event as CustomEvent<{
        text?: string;
        url?: string;
        durationMs?: number;
      }>;
      const detail = customEvent.detail ?? {};
      const text = detail.text ?? 'Probefahrt buchen';
      const url = detail.url ?? 'https://www.cupraofficial.de/kontakt/probefahrt';
      const durationMs = typeof detail.durationMs === 'number' ? detail.durationMs : 20000;
      digitalHumanStore.displayQRCode(text, url, durationMs);
    };

    window.addEventListener('cupra-show-qr', onShowQr as EventListener);
    return () => {
      window.removeEventListener('cupra-show-qr', onShowQr as EventListener);
    };
  }, [digitalHumanStore]);

  useEffect(() => {
    return () => {
      if (pendingToolHintTimeoutRef.current) {
        clearTimeout(pendingToolHintTimeoutRef.current);
      }
    };
  }, []);

  
 // Run once on mount

  return (
    <div className="app">
      {digitalHumanStore.streamingState !== StreamingStateEnum.StreamConnected && (
        <ConnectScreen
          onConnectClick={onConnectClick}
          streamingState={digitalHumanStore.streamingState}
          errorMessage={audioError}
          statusMessage={digitalHumanStore.connectionStatusMessage}
          isStarting={isStarting}
        />
      )}

      <div className="relative h-full">
        <PixelStreamingWrapper
          initialSettings={{
            UseMic: true,
            ss: process.env.NEXT_PUBLIC_WS_ENDPOINT,
            StartVideoMuted: process.env.NEXT_PUBLIC_START_VIDEO_MUTED 
              ? JSON.parse(process.env.NEXT_PUBLIC_START_VIDEO_MUTED)
              : false,
            HoveringMouse: true,
            MaxReconnectAttempts: 3,
            ForceMonoAudio: true,
          }}
        />
        <div className="absolute bottom-4 right-4 z-[60]">
          <MicButton
            store={digitalHumanStore}
            connectionStatus={connectionStatus}
            addUserAudio={addUserAudio}
            cancelAssistantResponse={() => {
              a2fStreamer.cancelCurrentStream();
              cancelAssistantResponse();
            }}
            triggerSpokenMessage={triggerSpokenMessage}
            welcomeMessage={welcomeMessage}
          />
        </div>

        {digitalHumanStore.qrCodeVisible && digitalHumanStore.qrCodeUrl && (
          <div className="absolute bottom-24 right-4 z-[70] max-w-[220px] rounded-xl bg-black/70 p-4 text-white backdrop-blur">
            <div className="mb-2 text-sm font-semibold">{digitalHumanStore.qrCodeText}</div>
            <div className="rounded-md bg-white p-2">
              <QRCodeSVG value={digitalHumanStore.qrCodeUrl} size={160} />
            </div>
          </div>
        )}

        {pendingToolHint && (
          <div className="absolute bottom-24 left-4 z-[70] max-w-[360px] rounded-xl bg-black/70 px-4 py-3 text-sm text-white backdrop-blur">
            {pendingToolHint}
          </div>
        )}
        {digitalHumanStore.goalCardsVisible
          ? <GoalCardsOverlay store={digitalHumanStore} onCourseConfirmed={handleCourseConfirmed} />
          : <CourseCardsOverlay store={digitalHumanStore} />
        }
        {subtitleMode === 'keywords' && <KeywordOverlay store={digitalHumanStore} />}
        {subtitleMode === 'on' && <SubtitleOverlay store={digitalHumanStore} />}
        <ProgressTracker store={digitalHumanStore} />
        {/* <PhaseBanner store={digitalHumanStore} /> */}

        {/* CC / Highlights toggles */}
        <div className="pointer-events-auto absolute right-4 top-4 z-[90] flex flex-col items-end gap-1.5">
          <button
            onClick={() => setSubtitleMode(m => { const next = m === 'on' ? 'off' : 'on'; console.log(`[SubtitleMode] CC toggled: "${m}" → "${next}"`); return next; })}
            className={`rounded-full px-3 py-1 text-xs font-semibold tracking-wide transition-all duration-200 ${
              subtitleMode === 'on'
                ? 'bg-violet-600/80 text-white shadow-[0_0_8px_2px_rgba(139,92,246,0.4)]'
                : 'bg-white/10 text-white/40 hover:bg-white/20 hover:text-white/60'
            }`}
          >
            CC
          </button>
          <button
            onClick={() => setSubtitleMode(m => { const next = m === 'keywords' ? 'off' : 'keywords'; console.log(`[SubtitleMode] Highlights toggled: "${m}" → "${next}"`); return next; })}
            className={`rounded-full px-3 py-1 text-xs font-semibold tracking-wide transition-all duration-200 ${
              subtitleMode === 'keywords'
                ? 'bg-violet-600/80 text-white shadow-[0_0_8px_2px_rgba(139,92,246,0.4)]'
                : 'bg-white/10 text-white/40 hover:bg-white/20 hover:text-white/60'
            }`}
          >
            Highlights
          </button>
        </div>
      </div>
    </div>
  );
});
